import { IncidentSeverity, LabelType, Role } from '@piccolohealth/pbs-common';
import { ChakraV3, SegmentGroup } from '@piccolohealth/ui';
import React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedIncidentCategoryChooser } from '../../components/forms/HookedIncidentCategoryChooser';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedLabelChooser } from '../../components/forms/HookedLabelChooser';
import { HookedParticipantChooser } from '../../components/forms/HookedParticipantChooser';
import { HookedSelect } from '../../components/forms/HookedSelect';
import { HookedSingleDatepicker } from '../../components/forms/HookedSingleDatepicker';
import { HookedTextArea } from '../../components/forms/HookedTextArea';
import { HookedUserMultiChooser } from '../../components/forms/HookedUserMultiChooser';
import { HookedWitnessesControl } from '../../components/forms/HookedWitnessesControl';
import { IncidentSeverityTag, incidentSeverityDetails } from './IncidentSeverityTag';

export const IncidentForm = () => {
  const { setValue } = useFormContext();
  const participantId = useWatch({
    name: 'participant.id',
  });

  const ndisReportable = useController({ name: 'ndisReportableIncident' });

  React.useEffect(() => {
    if (participantId) {
      setValue('reviewers', undefined);
    }
  }, [participantId, setValue]);

  return (
    <FormStack>
      <FormStack orientation='horizontal' gap={8}>
        <HookedFormField
          name='severity'
          label='Severity'
          helperText='How severe was this incident?'
          required
        >
          <HookedSelect
            name='severity'
            size='sm'
            placeholder='Choose incident severity'
            optionVariant='tag'
            options={Object.values(IncidentSeverity).map((severity) => {
              const [color, label] = incidentSeverityDetails(severity);
              return {
                value: severity,
                raw: severity,
                label,
                color,
              };
            })}
            components={{
              Option: ({ option }) => {
                return <IncidentSeverityTag severity={option.raw} />;
              },
            }}
          />
        </HookedFormField>
      </FormStack>
      <FormStack orientation='horizontal'></FormStack>
      <FormStack orientation='horizontal'>
        <HookedFormField
          name='timestamp'
          label='Occurred on'
          helperText='When did the incident occur?'
          required
        >
          <HookedSingleDatepicker name='timestamp' size='sm' placeholder='Choose a date' />
        </HookedFormField>
        <HookedFormField
          name='setting'
          label='Occurred where'
          helperText='Where did the incident occur?'
          required
        >
          <HookedInput name='setting' size='sm' placeholder='Enter an incident setting' />
        </HookedFormField>
      </FormStack>

      <FormStack orientation='horizontal'>
        <HookedFormField
          name='participant'
          label='Participant'
          helperText='Was there a participant involved in the incident?'
        >
          <HookedParticipantChooser name='participant' placeholder='Choose a participant' />
        </HookedFormField>
        <HookedFormField
          name='ndisReportableIncident'
          label='NDIS reportable'
          helperText='Is the incident NDIS reportable?'
        >
          <SegmentGroup
            value={ndisReportable.field.value === true ? 'true' : 'false'}
            onChange={(value) => ndisReportable.field.onChange(value === 'true')}
            options={[
              {
                label: 'Yes',
                value: 'true',
              },
              {
                label: 'No',
                value: 'false',
              },
            ]}
            size='sm'
          />
        </HookedFormField>
      </FormStack>
      <FormStack orientation='horizontal'>
        <HookedFormField
          name='categoryId'
          label='Category'
          helperText='What category does this incident belong to?'
          required
        >
          <HookedIncidentCategoryChooser name='categoryId' size='sm' />
        </HookedFormField>
        <HookedFormField name='labelIds' label='Labels' helperText='Any labels you want to assign'>
          <HookedLabelChooser name='labelIds' type={LabelType.Incident} />
        </HookedFormField>
      </FormStack>
      <FormStack>
        <HookedFormField
          name='description'
          label='Description'
          helperText='Describe what happened in the incident'
          required
        >
          <HookedTextArea name='description' size='sm' placeholder='What happened?' minH='6lh' />
        </HookedFormField>
      </FormStack>

      <FormStack>
        <HookedFormField name='witnesses' label='Witnesses'>
          <ChakraV3.Stack w='full'>
            <HookedWitnessesControl name='witnesses' />
          </ChakraV3.Stack>
        </HookedFormField>
      </FormStack>

      <FormStack orientation='horizontal'>
        <HookedFormField
          name='reviewers'
          label='Reviewers'
          helperText='Who should review this incident?'
          required
        >
          <HookedUserMultiChooser
            name='reviewers'
            roles={[Role.TeamLeader, Role.Administrator]}
            participantId={participantId}
            size='sm'
            placeholder='Choose one or more incident reviewers'
          />
        </HookedFormField>
      </FormStack>
    </FormStack>
  );
};
